<script setup lang="ts">
import { computed } from 'vue';
import { AlertTriangle, ArrowDown, Check, X } from 'lucide-vue-next';

import { cn } from '@/lib/cn';
import type { ProposalState } from '@/composables/useCopilotConversation';

/**
 * Per ADR-0052. Inline action chip rendered in place of a proposal
 * anchor (`[label](#proposal-XYZ)`) inside an assistant message. The
 * chip *navigates* — clicking it smooth-scrolls the matching proposal
 * card into view and triggers a one-shot pulse highlight. It does NOT
 * apply the proposal: the user still confirms on the card so the
 * high-stakes-confirmation guarantees from ADR-0043 hold.
 *
 * State semantics:
 *   - `null` state → the model emitted an anchor that doesn't match
 *     any rendered proposal (hallucination, or proposal failed before
 *     commit). Renders as a muted chip with a warning icon and is
 *     inert on click — the prose stays readable.
 *   - `pending` / `applying` → accent-tinted, clickable, points at the
 *     card with a one-shot pulse.
 *   - `applied` / `silent_applied` → success-tinted with a check.
 *   - `rejected` / `reverted` / `failed` → muted, still clickable so
 *     the user can re-read the card if curious.
 */
const props = defineProps<{
  proposalId: string;
  label: string;
  state: ProposalState | null;
}>();

const status = computed(() => props.state?.status ?? null);

const tone = computed<'pending' | 'applied' | 'muted' | 'orphan'>(() => {
  if (!props.state) return 'orphan';
  switch (status.value) {
    case 'pending':
    case 'applying':
      return 'pending';
    case 'applied':
    case 'silent_applied':
      return 'applied';
    case 'rejected':
    case 'reverted':
    case 'failed':
      return 'muted';
    default:
      return 'muted';
  }
});

const containerClass = computed(() =>
  cn(
    'inline-flex items-center gap-1 px-1.5 py-0.5 mx-0.5 rounded-md align-baseline',
    'text-[13px] font-medium transition-colors',
    'border focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/40',
    // Min tappable target for mobile while staying inline-friendly on
    // desktop. ADR-0052 §"Open" flags 44px as the ideal — we get there
    // through the surrounding line-height plus generous click padding.
    'min-h-[24px]',
    tone.value === 'pending' &&
      'bg-accent/10 text-accent border-accent/30 hover:bg-accent/15 cursor-pointer',
    tone.value === 'applied' &&
      'bg-success/10 text-success border-success/30 hover:bg-success/15 cursor-pointer',
    tone.value === 'muted' && 'bg-bg text-text-muted border-border hover:bg-surface cursor-pointer',
    tone.value === 'orphan' && 'bg-warning/5 text-text-muted border-warning/30 cursor-default',
  ),
);

function pulseTarget(target: HTMLElement): void {
  target.classList.remove('proposal-card-pulse');
  // Force a reflow so re-adding the class restarts the animation.
  void target.offsetWidth;
  target.classList.add('proposal-card-pulse');
  window.setTimeout(() => target.classList.remove('proposal-card-pulse'), 1400);
}

function onClick(): void {
  if (tone.value === 'orphan') return;
  const target = document.querySelector<HTMLElement>(
    `[data-proposal-card-id="${props.proposalId}"]`,
  );
  if (!target) return;
  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  pulseTarget(target);
}
</script>

<template>
  <button
    type="button"
    :class="containerClass"
    :data-proposal-anchor="proposalId"
    :data-state="status ?? 'orphan'"
    :aria-label="label"
    :disabled="tone === 'orphan'"
    @click.stop="onClick"
  >
    <Check v-if="tone === 'applied'" class="h-3 w-3 shrink-0" />
    <AlertTriangle v-else-if="tone === 'orphan'" class="h-3 w-3 shrink-0" />
    <X
      v-else-if="status === 'rejected' || status === 'reverted' || status === 'failed'"
      class="h-3 w-3 shrink-0"
    />
    <ArrowDown v-else class="h-3 w-3 shrink-0" />
    <span class="truncate max-w-[24ch]">{{ label }}</span>
  </button>
</template>

<style scoped>
@keyframes proposal-card-pulse {
  0% {
    box-shadow: 0 0 0 0 rgba(var(--color-accent-rgb, 37 99 235), 0.45);
  }
  60% {
    box-shadow: 0 0 0 8px rgba(var(--color-accent-rgb, 37 99 235), 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(var(--color-accent-rgb, 37 99 235), 0);
  }
}

:global(.proposal-card-pulse) {
  animation: proposal-card-pulse 1.4s ease-out;
}
</style>
